import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../../../services';
import { ApiResponse } from '../../../types/api';

interface AgendamentoConfig {
  ativo: boolean;
  dias_semana: string[];
  horarios: string[];
}

interface WhatsAppConnection {
  id: number;
  nome: string;
  numero_telefone: string;
  status: string;
  empresa_nome?: string;
}

interface WhatsAppConfig {
  numero_padrao: string;
  conexao_ativa: boolean;
  conexoes_selecionadas: number[]; // IDs das conexões selecionadas
}

interface ChecklistPersonalizado {
  id?: number;
  veiculo_id: number;
  ativo: boolean;
  grupos: any[];
  agendamento_automatico: AgendamentoConfig;
  whatsapp_config: WhatsAppConfig;
}

const VehicleAgendamentos: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [checklist, setChecklist] = useState<ChecklistPersonalizado | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [whatsappConnections, setWhatsappConnections] = useState<WhatsAppConnection[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(false);

  const diasSemana = [
    { value: 'segunda', label: 'Segunda-feira' },
    { value: 'terca', label: 'Terça-feira' },
    { value: 'quarta', label: 'Quarta-feira' },
    { value: 'quinta', label: 'Quinta-feira' },
    { value: 'sexta', label: 'Sexta-feira' },
    { value: 'sabado', label: 'Sábado' },
    { value: 'domingo', label: 'Domingo' }
  ];

  const horariosDisponiveis = [
    '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
    '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'
  ];

  const loadWhatsappConnections = async () => {
    try {
      setLoadingConnections(true);
      const response = await apiService.getWhatsAppConnections();
      
      if (response.success && response.data) {
        setWhatsappConnections(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar conexões WhatsApp:', error);
    } finally {
      setLoadingConnections(false);
    }
  };

  const loadChecklist = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Verificar se o ID é válido (não NaN ou undefined)
      const vehicleId = Number(id);
      if (isNaN(vehicleId) || !id) {
        // Para criação de veículo, usar estado vazio
        setChecklist({
          veiculo_id: 0, // ID temporário para criação
          ativo: false,
          grupos: [],
          agendamento_automatico: {
            ativo: false,
            dias_semana: [],
            horarios: []
          },
          whatsapp_config: {
            numero_padrao: '',
            conexao_ativa: false,
            conexoes_selecionadas: []
          }
        });
        setIsLoading(false);
        return;
      }
      
      const response = await apiService.getVeiculoChecklistAdaptadoByVehicle(vehicleId) as ApiResponse<any>;
      
      if (response.success && response.data) {
        // Garantir que conexoes_selecionadas existe
        const checklistData = response.data;
        if (!checklistData.whatsapp_config.conexoes_selecionadas) {
          checklistData.whatsapp_config.conexoes_selecionadas = [];
        }
        setChecklist(checklistData);
      } else {
        // Se não existe checklist, criar um padrão
        setChecklist({
          veiculo_id: vehicleId,
          ativo: false,
          grupos: [],
          agendamento_automatico: {
            ativo: false,
            dias_semana: [],
            horarios: []
          },
          whatsapp_config: {
            numero_padrao: '',
            conexao_ativa: false,
            conexoes_selecionadas: []
          }
        });
      }
    } catch (error) {
      console.error('Erro ao carregar checklist:', error);
      setError('Erro ao carregar configurações de agendamento');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadChecklist();
    loadWhatsappConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const saveAgendamento = async () => {
    if (!checklist) return;

    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      // Verificar se é criação de veículo (veiculo_id = 0)
      if (checklist.veiculo_id === 0) {
        setSuccess('Configurações de agendamento preparadas para salvar com o veículo!');
        setIsSaving(false);
        return;
      }

      let response;
      if (checklist.id) {
        // Atualizar checklist existente
        response = await apiService.updateVeiculoChecklistAdaptado(checklist.id, checklist);
      } else {
        // Criar novo checklist
        response = await apiService.createVeiculoChecklistAdaptado(checklist);
      }

      if (response.success) {
        setSuccess('Configurações de agendamento salvas com sucesso!');
        // Recarregar dados atualizados
        loadChecklist();
      } else {
        throw new Error(response.message || 'Erro ao salvar configurações');
      }
    } catch (error) {
      console.error('Erro ao salvar agendamento:', error);
      setError('Erro ao salvar configurações de agendamento');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleDiaSemana = (dia: string) => {
    if (!checklist) return;

    const diasAtuais = [...checklist.agendamento_automatico.dias_semana];
    const index = diasAtuais.indexOf(dia);
    
    if (index > -1) {
      diasAtuais.splice(index, 1);
    } else {
      diasAtuais.push(dia);
    }

    setChecklist({
      ...checklist,
      agendamento_automatico: {
        ...checklist.agendamento_automatico,
        dias_semana: diasAtuais
      }
    });
  };

  const toggleHorario = (horario: string) => {
    if (!checklist) return;

    const horariosAtuais = [...checklist.agendamento_automatico.horarios];
    const index = horariosAtuais.indexOf(horario);
    
    if (index > -1) {
      horariosAtuais.splice(index, 1);
    } else {
      horariosAtuais.push(horario);
    }

    setChecklist({
      ...checklist,
      agendamento_automatico: {
        ...checklist.agendamento_automatico,
        horarios: horariosAtuais
      }
    });
  };

  const toggleWhatsappConnection = (connectionId: number) => {
    if (!checklist) return;

    const conexoesAtuais = [...checklist.whatsapp_config.conexoes_selecionadas];
    const index = conexoesAtuais.indexOf(connectionId);
    
    if (index > -1) {
      conexoesAtuais.splice(index, 1);
    } else {
      conexoesAtuais.push(connectionId);
    }

    setChecklist({
      ...checklist,
      whatsapp_config: {
        ...checklist.whatsapp_config,
        conexoes_selecionadas: conexoesAtuais
      }
    });
  };

  const updateWhatsappConfig = (field: keyof WhatsAppConfig, value: any) => {
    if (!checklist) return;

    setChecklist({
      ...checklist,
      whatsapp_config: {
        ...checklist.whatsapp_config,
        [field]: value
      }
    });
  };

  const updateAgendamentoConfig = (field: keyof AgendamentoConfig, value: any) => {
    if (!checklist) return;

    setChecklist({
      ...checklist,
      agendamento_automatico: {
        ...checklist.agendamento_automatico,
        [field]: value
      }
    });
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string, className: string }> = {
      'conectado': { label: 'Conectado', className: 'status-active' },
      'conectando': { label: 'Conectando', className: 'status-warning' },
      'desconectado': { label: 'Desconectado', className: 'status-inactive' }
    };

    const statusInfo = statusMap[status] || { label: status, className: 'status-inactive' };
    
    return (
      <span className={`status-badge ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#04C64F', marginBottom: '20px' }}></i>
          <p>Carregando configurações de agendamento...</p>
        </div>
      </div>
    );
  }

  if (!checklist) {
    return (
      <div className="card">
        <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '2rem', color: '#e74c3c', marginBottom: '20px' }}></i>
          <p>Erro ao carregar configurações de agendamento.</p>
          <button className="btn btn-primary" onClick={loadChecklist}>
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Alertas */}
      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success" role="alert">
          <i className="fas fa-check-circle"></i>
          {success}
        </div>
      )}

      {/* Configuração de Agendamento Automático */}
      <div className="card">
        <div className="card-header">
          <h3 style={{ margin: 0, color: '#2c3e50' }}>
            <i className="fas fa-clock"></i>
            Agendamento Automático de Checklists
          </h3>
        </div>
        <div className="card-body">
          <div className="form-group">
            <div className="form-check form-switch">
              <input
                type="checkbox"
                className="form-check-input"
                id="agendamento-ativo"
                checked={checklist.agendamento_automatico.ativo}
                onChange={(e) => updateAgendamentoConfig('ativo', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="agendamento-ativo">
                <strong>Ativar Agendamento Automático</strong>
              </label>
            </div>
            <small className="form-text text-muted">
              Quando ativado, o sistema enviará automaticamente checklists nos dias e horários configurados.
            </small>
          </div>

          {checklist.agendamento_automatico.ativo && (
            <div style={{ marginTop: '20px' }}>
              {/* Dias da Semana */}
              <div className="form-group">
                <label className="form-label">
                  <strong>Dias da Semana</strong>
                </label>
                <div className="dias-semana-grid" style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                  gap: '10px',
                  marginTop: '10px'
                }}>
                  {diasSemana.map((dia) => (
                    <div key={dia.value} className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`dia-${dia.value}`}
                        checked={checklist.agendamento_automatico.dias_semana.includes(dia.value)}
                        onChange={() => toggleDiaSemana(dia.value)}
                      />
                      <label className="form-check-label" htmlFor={`dia-${dia.value}`}>
                        {dia.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Horários */}
              <div className="form-group" style={{ marginTop: '20px' }}>
                <label className="form-label">
                  <strong>Horários de Envio</strong>
                </label>
                <div className="horarios-grid" style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', 
                  gap: '8px',
                  marginTop: '10px'
                }}>
                  {horariosDisponiveis.map((horario) => (
                    <div key={horario} className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`horario-${horario}`}
                        checked={checklist.agendamento_automatico.horarios.includes(horario)}
                        onChange={() => toggleHorario(horario)}
                      />
                      <label className="form-check-label" htmlFor={`horario-${horario}`}>
                        {horario}
                      </label>
                    </div>
                  ))}
                </div>
                <small className="form-text text-muted">
                  Selecione os horários em que os checklists serão enviados automaticamente.
                </small>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Configuração WhatsApp */}
      <div className="card" style={{ marginTop: '20px' }}>
        <div className="card-header">
          <h3 style={{ margin: 0, color: '#2c3e50' }}>
            <i className="fab fa-whatsapp"></i>
            Configuração WhatsApp
          </h3>
        </div>
        <div className="card-body">
          {/* Conexões WhatsApp Disponíveis */}
          <div className="form-group">
            <label className="form-label">
              <strong>Conexões WhatsApp Disponíveis</strong>
            </label>
            {loadingConnections ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <i className="fas fa-spinner fa-spin" style={{ color: '#04C64F' }}></i>
                <p>Carregando conexões WhatsApp...</p>
              </div>
            ) : whatsappConnections.length > 0 ? (
              <div className="whatsapp-connections-grid" style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                gap: '15px',
                marginTop: '10px'
              }}>
                {whatsappConnections.map((connection) => (
                  <div key={connection.id} className="connection-card" style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '15px',
                    backgroundColor: '#f8f9fa'
                  }}>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`connection-${connection.id}`}
                        checked={checklist.whatsapp_config.conexoes_selecionadas.includes(connection.id)}
                        onChange={() => toggleWhatsappConnection(connection.id)}
                      />
                      <label className="form-check-label" htmlFor={`connection-${connection.id}`} style={{ fontWeight: 'bold' }}>
                        {connection.nome}
                      </label>
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '0.9rem' }}>
                      <div><strong>Número:</strong> {connection.numero_telefone}</div>
                      <div><strong>Status:</strong> {getStatusBadge(connection.status)}</div>
                      {connection.empresa_nome && (
                        <div><strong>Empresa:</strong> {connection.empresa_nome}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
                <i className="fas fa-exclamation-circle" style={{ fontSize: '2rem', marginBottom: '10px' }}></i>
                <p>Nenhuma conexão WhatsApp cadastrada no sistema.</p>
                <small>
                  Configure conexões WhatsApp em Configurações → WhatsApp antes de usar o agendamento automático.
                </small>
              </div>
            )}
            <small className="form-text text-muted">
              Selecione uma ou mais conexões WhatsApp que serão usadas para envio automático dos checklists.
            </small>
          </div>

          {/* Número WhatsApp Padrão (para compatibilidade) */}
          <div className="form-group" style={{ marginTop: '20px' }}>
            <label className="form-label">
              <strong>Número WhatsApp Padrão</strong>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Ex: 5511999999999"
              value={checklist.whatsapp_config.numero_padrao}
              onChange={(e) => updateWhatsappConfig('numero_padrao', e.target.value)}
            />
            <small className="form-text text-muted">
              Digite o número no formato internacional (código do país + DDD + número)
            </small>
          </div>

          <div className="form-group" style={{ marginTop: '15px' }}>
            <div className="form-check form-switch">
              <input
                type="checkbox"
                className="form-check-input"
                id="whatsapp-ativo"
                checked={checklist.whatsapp_config.conexao_ativa}
                onChange={(e) => updateWhatsappConfig('conexao_ativa', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="whatsapp-ativo">
                <strong>Conexão WhatsApp Ativa</strong>
              </label>
            </div>
            <small className="form-text text-muted">
              Quando ativado, o sistema usará as conexões WhatsApp selecionadas para envio automático.
            </small>
          </div>
        </div>
      </div>

      {/* Resumo da Configuração */}
      {checklist.agendamento_automatico.ativo && (
        <div className="card" style={{ marginTop: '20px', backgroundColor: '#f8f9fa' }}>
          <div className="card-header">
            <h4 style={{ margin: 0, color: '#2c3e50' }}>
              <i className="fas fa-info-circle"></i>
              Resumo da Configuração
            </h4>
          </div>
          <div className="card-body">
            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
              <div className="info-item">
                <label className="form-label">Status do Agendamento</label>
                <span className="info-value">
                  <span className="status-badge status-active">Ativo</span>
                </span>
              </div>

              <div className="info-item">
                <label className="form-label">Dias Selecionados</label>
                <span className="info-value">
                  {checklist.agendamento_automatico.dias_semana.length > 0 
                    ? checklist.agendamento_automatico.dias_semana
                        .map(dia => diasSemana.find(d => d.value === dia)?.label)
                        .join(', ')
                    : 'Nenhum dia selecionado'}
                </span>
              </div>

              <div className="info-item">
                <label className="form-label">Horários Selecionados</label>
                <span className="info-value">
                  {checklist.agendamento_automatico.horarios.length > 0 
                    ? checklist.agendamento_automatico.horarios.join(', ')
                    : 'Nenhum horário selecionado'}
                </span>
              </div>

              <div className="info-item">
                <label className="form-label">Conexões Selecionadas</label>
                <span className="info-value">
                  {checklist.whatsapp_config.conexoes_selecionadas.length > 0 
                    ? checklist.whatsapp_config.conexoes_selecionadas
                        .map(id => whatsappConnections.find(c => c.id === id)?.nome)
                        .filter(Boolean)
                        .join(', ')
                    : 'Nenhuma conexão selecionada'}
                </span>
              </div>

              <div className="info-item">
                <label className="form-label">Status WhatsApp</label>
                <span className="info-value">
                  <span className={`status-badge ${checklist.whatsapp_config.conexao_ativa ? 'status-active' : 'status-inactive'}`}>
                    {checklist.whatsapp_config.conexao_ativa ? 'Ativo' : 'Inativo'}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Botão Salvar */}
      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          className="btn btn-primary"
          onClick={saveAgendamento}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              Salvando...
            </>
          ) : (
            <>
              <i className="fas fa-save"></i>
              Salvar Configurações
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default VehicleAgendamentos;
